import { QueryClient } from '@tanstack/react-query'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
})

// Query keys factory for consistent key management
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
  },
  
  // Campaigns
  campaigns: {
    all: ['campaigns'] as const,
    lists: () => [...queryKeys.campaigns.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.campaigns.lists(), filters ?? {}] as const,
    details: () => [...queryKeys.campaigns.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.campaigns.details(), id] as const,
    stats: (id: string) => [...queryKeys.campaigns.detail(id), 'stats'] as const,
  },

  // A/B Tests
  abTests: {
    all: ['abTests'] as const,
    list: (campaignId: string) => [...queryKeys.abTests.all, 'list', campaignId] as const,
    detail: (campaignId: string, testId: string) => [...queryKeys.abTests.all, 'detail', campaignId, testId] as const,
    results: (campaignId: string, testId: string) => [...queryKeys.abTests.all, 'results', campaignId, testId] as const,
  },

  // Offers
  offers: {
    all: ['offers'] as const,
    lists: () => [...queryKeys.offers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.offers.lists(), filters ?? {}] as const,
    details: () => [...queryKeys.offers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.offers.details(), id] as const,
  },
  
  // Landings
  landings: {
    all: ['landings'] as const,
    lists: () => [...queryKeys.landings.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.landings.lists(), filters] as const,
    details: () => [...queryKeys.landings.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.landings.details(), id] as const,
  },
  
  // Reports
  reports: {
    all: ['reports'] as const,
    data: (params: Record<string, unknown>) => [...queryKeys.reports.all, 'data', params] as const,
  },
  
  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    realtime: () => [...queryKeys.dashboard.all, 'realtime'] as const,
  },
  
  // Billing
  billing: {
    all: ['billing'] as const,
    subscription: () => [...queryKeys.billing.all, 'subscription'] as const,
    invoices: () => [...queryKeys.billing.all, 'invoices'] as const,
    usage: () => [...queryKeys.billing.all, 'usage'] as const,
  },
  
  // Settings
  settings: {
    all: ['settings'] as const,
    profile: () => [...queryKeys.settings.all, 'profile'] as const,
    team: () => [...queryKeys.settings.all, 'team'] as const,
    domains: () => [...queryKeys.settings.all, 'domains'] as const,
    apiKeys: () => [...queryKeys.settings.all, 'apiKeys'] as const,
  },

  // Sites
  sites: {
    all: ['sites'] as const,
    lists: () => [...queryKeys.sites.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.sites.lists(), filters ?? {}] as const,
    details: () => [...queryKeys.sites.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.sites.details(), id] as const,
    pages: (siteId: string) => [...queryKeys.sites.detail(siteId), 'pages'] as const,
    templates: () => [...queryKeys.sites.all, 'templates'] as const,
  },

  // Variables
  variables: {
    all: ['variables'] as const,
    list: () => [...queryKeys.variables.all, 'list'] as const,
  },

  // Team cloaking defaults
  teamCloaking: {
    all: ['teamCloaking'] as const,
    defaults: () => [...queryKeys.teamCloaking.all, 'defaults'] as const,
  },
}
